import { useEffect, useContext as useReactContext, useRef } from 'react';

import { EditClassroomCodeDialog, EditClassroomCodeDialogHandle } from 'pages/students/components/Groups/components/edit_classroom_code_dialog/EditClassroomCodeDialog';
import { GroupGMetrixTableDialog, GroupGMetrixTableDialogHandle } from 'components/GroupGMetrixTableDialog/GroupGMetrixTableDialog';
import { StudentDetailsContext, StudentsContext } from 'pages/students/context/StudentsDefaultContext';
import { GMetrixButtonDropdown } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { filterDataBySearchTerm } from 'utils';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'components/Tabs';
import { GMetrixTable } from 'components';
import { useJoyride } from 'app/joyride';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/students/components/Groups/styles/groups.scss';

type GroupsProps = {
    groups?: StudentGroupWithDetails[];
    studentId: number;
}

export const Groups = (props: GroupsProps) => {

    const { groups, studentId } = props;

    const [state, setState] = useSuperState({
        loadingGridData: !groups,
        searchTerm: ''
    });

    const { isSystemMode, userId } = useApp();
    const { pageContext, updateById } = useContext(StudentDetailsContext);
    const { permissions } = useReactContext(StudentsContext);
    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const { loadCurrentTour } = useJoyride();

    const navigate = useNavigate();

    const gmetrixConfirmRef = useRef<any>(null);
    const groupDialogRef = useRef<GroupGMetrixTableDialogHandle>(null);
    const editClassroomCodeDialogRef = useRef<EditClassroomCodeDialogHandle>(null);

    const removeStudentFromGroup = async (dataItem: StudentGroupWithDetails | undefined) => {

        if (dataItem && pageContext) {

            await StudentGroupApi.DeleteStudentFromGroup(dataItem.Id, [pageContext.Id]);

            try {

                const studentGroups = await StudentGroupApi.GetAll('Student', studentId);
                updateById(studentId, { groups: studentGroups });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_reload_student_classrooms.4a9b660d', defaultMessage: 'Failed to reload student classrooms.' }))(e);

            }

        }

    };

    const onDetailsClick = (classroomId: number) => {

        navigate(`/classrooms/${classroomId}`);

    };

    const reloadGroups = async () => {

        try {

            const studentGroups = await StudentGroupApi.GetAll('Student', studentId);
            updateById(studentId, { groups: studentGroups });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_reload_student_classrooms.4a9b660d', defaultMessage: 'Failed to reload student classrooms.' }))(e);

        }

    };

    useEffect(() => {

        loadCurrentTour('ClassroomsTab');

    }, []);

    useEffect(() => {

        const loadGroups = async () => {

            try {

                const studentGroups = await StudentGroupApi.GetAll('Student', studentId);
                updateById(studentId, { groups: studentGroups });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_student_classrooms.d4c5af81', defaultMessage: 'Failed to load student classrooms.' }))(e);

            } finally {

                setState({ loadingGridData: false });

            }

        };
        loadGroups();

    }, []);

    const handleAddGroups = async () => {

        await groupDialogRef.current?.show({ option: 'Add' });

        // Reload groups after dialog closes.
        try {

            const studentGroups = await StudentGroupApi.GetAll('Student', studentId);
            updateById(studentId, { groups: studentGroups });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_student_classrooms.d4c5af81', defaultMessage: 'Failed to load student classrooms.' }))(e);

        }

    };

    return (
        <div className="groups">
            <TileContainer
                addButton
                showButtonAsDropDown
                buttonDropDownItemList={{
                    itemActionList: [handleAddGroups],
                    itemTextList: [formatMessage({ id: 'add_existing_classrooms.a91fba45', defaultMessage: 'Add Existing Classrooms' })]
                }}
                addButtonDisabled={permissions!.PartnerType !== 4}
                addButtonDisabledTooltip={permissions!.PartnerType !== 4 ? 'Adding classrooms is not available for this partner type.' : undefined}
                searchOptions={{ searchTerm: state.searchTerm, setSearchTerm: searchTerm => setState({ searchTerm }) }}
                showButtonAsDropDownText={formatMessage({ id: 'new_classroom.02c85b91', defaultMessage: 'New Classroom' })}
            >
                <GMetrixTable
                    allowExport
                    columnData={[
                        { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                        {
                            cell: ({ dataItem }) => (
                                <td className="">
                                    {dataItem.Partner?.Name}
                                </td>
                            ),
                            field: 'Partner',
                            title: formatMessage({ id: 'partner_name.4cf8979c', defaultMessage: 'Partner Name' })
                        },
                        {
                            cell: ({ dataItem }) => (
                                <td className="">
                                    {dataItem.StudentGroupStudents && dataItem.StudentGroupStudents[0].DateAddedString}
                                </td>
                            ),
                            field: 'StudentGroupStudents',
                            title: formatMessage({ id: 'date_added.88c093e2', defaultMessage: 'Date Added' })
                        },
                        {
                            cell: ({ dataItem }) => (
                                <td
                                    className={`${dataItem.ExpirationDate && new Date(dataItem.ExpirationDate) < new Date()
                                        ? 'text-danger fw-bold'
                                        : ''
                                        }`}
                                >
                                    {dataItem.ExpirationDate
                                        ? new Date(dataItem.ExpirationDate).toLocaleDateString()
                                        : ''}
                                </td>
                            ),
                            field: 'ExpirationDate',
                            title: formatMessage({ id: 'expires.84d49534', defaultMessage: 'Expires' })
                        },
                        { field: 'Description', title: formatMessage({ id: 'description.e1c24171', defaultMessage: 'Description', description: 'Formulario' }) },
                        {
                            cell: ({ dataItem }) => (
                                <td>
                                    <div className="d-flex justify-content-end mt-2 mb-2">
                                        <GMetrixButtonDropdown
                                            connectedButton={(
                                                <GMetrixButton
                                                    className="groups__multi-button"
                                                    onClick={() => onDetailsClick(dataItem.Id)}
                                                    variant="primary"
                                                >
                                                    <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                                                </GMetrixButton>
                                            )}
                                            dataItems={[
                                                ...(isSystemMode ? [{
                                                    action: () => {

                                                        editClassroomCodeDialogRef.current?.show(dataItem.Id, studentId, dataItem.Control, dataItem.Name);

                                                    }, text: formatMessage({ id: 'edit.00978daf', defaultMessage: 'Edit' })
                                                }] : []),
                                                {
                                                    action: async () => {

                                                        const result = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'remove_student_from_classroom.647e1266', defaultMessage: 'Remove Student from Classroom' }), formatMessage({ id: 'are_you_sure_you_want_to_remove_this_stu.5f0e6bd6', defaultMessage: 'Are you sure you want to remove this Student from this Classroom?' }));
                                                        if (result) {

                                                            removeStudentFromGroup(dataItem);

                                                        }

                                                    }, text: formatMessage({ id: 'remove.d34ac1e7', defaultMessage: 'Remove' })
                                                }
                                            ]}
                                            className="groups__multi-button-right"
                                            iconClass="fa-solid fa-ellipsis-v"
                                            variant="primary"
                                        />
                                    </div>
                                </td>
                            ),
                            width: 200
                        }
                    ]}
                    bottomBarTableName={formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' })}
                    loading={state.loadingGridData}
                    rowData={groups ? filterDataBySearchTerm(state.searchTerm, groups) : []}
                />
            </TileContainer>
            <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

            <GroupGMetrixTableDialog
                multiSelect
                groupAssociation={{
                    associationIds: pageContext ? [pageContext.Id] : [],
                    associationName: 'Student'
                }}
                actionButtonText={formatMessage({ id: 'add_classrooms.44465172', defaultMessage: 'Add Classroom(s)' })}
                currentUserId={userId}
                option="Add"
                ref={groupDialogRef}
            />

            <EditClassroomCodeDialog
                onSaved={reloadGroups}
                ref={editClassroomCodeDialogRef}
            />

        </div>
    );

};
